<template>
  <div
    class="relative flex h-36 w-36 items-center justify-center overflow-hidden"
    aria-hidden="true"
    data-testid="scanner-success-animation"
  >
    <div
      v-if="!isRiveReady"
      class="absolute inset-0 flex items-center justify-center"
      data-testid="scanner-success-static-tick"
    >
      <ion-icon
        :icon="checkmark"
        class="text-[7rem] text-white [--ionicon-stroke-width:56px]"
      ></ion-icon>
    </div>
    <canvas
      ref="riveCanvas"
      :class="[
        'h-full w-full transition-opacity duration-150',
        isRiveReady ? 'opacity-100' : 'opacity-0',
      ]"
      data-testid="scanner-success-rive"
    ></canvas>
  </div>
</template>

<script>
import { checkmark } from 'ionicons/icons';
import {
  getScannerSuccessRiveSource,
  loadScannerSuccessRiveRuntime,
} from '@/utils/scanner-success-animation';

const SPLASH_WAIT_TIMEOUT_MS = 5000;

export default {
  name: 'ScannerSuccessAnimation',
  data() {
    return {
      riveAnimation: null,
      resizeHandler: null,
      isRiveReady: false,
      animationStartCancelled: false,
      checkmark,
    };
  },
  mounted() {
    this.startSuccessAnimation();
  },
  beforeUnmount() {
    this.animationStartCancelled = true;
    this.cleanupRiveAnimation();
  },
  methods: {
    async startSuccessAnimation() {
      await this.waitForAppSplashToClear();
      if (this.animationStartCancelled) return;

      this.playSuccessAnimation();
    },
    waitForAppSplashToClear() {
      if (typeof document === 'undefined') return Promise.resolve();

      const startedAt = Date.now();
      const scheduleFrame =
        typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame
          : callback => setTimeout(callback, 16);

      return new Promise(resolve => {
        const checkSplash = () => {
          const splashIsVisible = Boolean(document.querySelector('.splash-overlay'));
          const timedOut = Date.now() - startedAt >= SPLASH_WAIT_TIMEOUT_MS;

          if (this.animationStartCancelled || !splashIsVisible || timedOut) {
            resolve();
            return;
          }

          scheduleFrame(checkSplash);
        };

        checkSplash();
      });
    },
    async playSuccessAnimation() {
      if (!this.$refs.riveCanvas || this.riveAnimation) return;

      try {
        const [{ Alignment, Fit, Layout, Rive }, riveSource] = await Promise.all([
          loadScannerSuccessRiveRuntime(),
          getScannerSuccessRiveSource(),
        ]);
        if (!this.$refs.riveCanvas) return;

        this.riveAnimation = new Rive({
          ...riveSource,
          canvas: this.$refs.riveCanvas,
          autoplay: true,
          layout: new Layout({
            fit: Fit.Contain,
            alignment: Alignment.Center,
          }),
          onLoad: () => {
            this.resizeRiveCanvas();
            this.isRiveReady = true;
          },
        });

        this.resizeHandler = () => this.resizeRiveCanvas();
        window.addEventListener('resize', this.resizeHandler);
      } catch {
        // Keep the success state usable even if the animation runtime fails to load.
      }
    },
    resizeRiveCanvas() {
      this.riveAnimation?.resizeDrawingSurfaceToCanvas();
    },
    cleanupRiveAnimation() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }

      if (!this.riveAnimation) return;

      this.riveAnimation.cleanup();
      this.riveAnimation = null;
      this.isRiveReady = false;
    },
  },
};
</script>
